import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import {
  PASSWORD_MIN_LENGTH,
  PasswordStrength,
  estimatePasswordStrength,
} from './password-strength';

/** Colore dei segmenti pieni per ciascun livello: accanto c'è sempre la parola. */
const SEGMENT_COLORS: Record<PasswordStrength, string> = {
  debole: 'bg-red-600 dark:bg-red-500',
  sufficiente: 'bg-amber-500',
  buona: 'bg-emerald-600 dark:bg-emerald-500',
  ottima: 'bg-emerald-600 dark:bg-emerald-500',
};

interface Requirement {
  readonly id: string;
  readonly label: string;
  /** `null`: requisito solo informativo, verificato dal server al salvataggio. */
  readonly met: boolean | null;
}

/**
 * Misuratore della robustezza della password: quattro segmenti con il livello a parole (debole,
 * sufficiente, buona, ottima) ed elenco dei requisiti. La stima è locale; password comuni e
 * violate le controlla il server.
 */
@Component({
  selector: 'bs-strength-meter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'flex flex-col gap-3' },
  template: `
    <div class="flex items-center gap-3" [attr.data-testid]="testId()">
      <div class="grid flex-1 grid-cols-4 gap-1.5" aria-hidden="true">
        @for (segment of segments; track segment) {
          <span
            [class]="segmentClass(segment)"
            [attr.data-filled]="segment <= estimate().score"
          ></span>
        }
      </div>
      <p
        class="w-36 shrink-0 text-sm font-medium text-ink dark:text-stone-100"
        aria-live="polite"
        [attr.data-testid]="testId() ? testId() + '-level' : null"
      >
        @if (password()) {
          Robustezza: {{ estimate().level }}
        } @else {
          <span class="text-stone-500 dark:text-stone-400">Robustezza</span>
        }
      </p>
    </div>
    <ul class="flex flex-col gap-1.5 text-sm">
      @for (requirement of requirements(); track requirement.id) {
        <li
          class="flex items-start gap-2"
          [attr.data-requirement]="requirement.id"
          [attr.data-met]="requirement.met"
        >
          @if (requirement.met === null) {
            <bs-icon name="info" [size]="18" class="text-stone-500 dark:text-stone-400" />
            <span class="text-stone-600 dark:text-stone-300">
              {{ requirement.label }}<span class="sr-only"> (verificato al salvataggio)</span>
            </span>
          } @else if (requirement.met) {
            <bs-icon
              name="check-circle"
              [size]="18"
              class="text-emerald-700 dark:text-emerald-400"
            />
            <span class="text-ink dark:text-stone-100">
              {{ requirement.label }}<span class="sr-only"> (soddisfatto)</span>
            </span>
          } @else {
            <bs-icon name="circle" [size]="18" class="text-stone-400 dark:text-stone-500" />
            <span class="text-stone-600 dark:text-stone-300">
              {{ requirement.label }}<span class="sr-only"> (non ancora soddisfatto)</span>
            </span>
          }
        </li>
      }
    </ul>
  `,
})
export class StrengthMeterComponent {
  readonly password = input.required<string>();
  /** Email, nome e cognome dell'utente: la password non deve contenerli. */
  readonly personalValues = input<readonly string[]>([]);
  /** Lunghezza minima richiesta: quella del server, se diversa dalla predefinita. */
  readonly minLength = input(PASSWORD_MIN_LENGTH);
  readonly testId = input<string | null>(null);

  protected readonly segments = [1, 2, 3, 4] as const;
  protected readonly estimate = computed(() =>
    estimatePasswordStrength(this.password(), this.personalValues(), this.minLength()),
  );

  protected readonly requirements = computed<readonly Requirement[]>(() => {
    const empty = this.password().length === 0;
    const estimate = this.estimate();
    return [
      {
        id: 'length',
        label: `Almeno ${this.minLength()} caratteri`,
        met: estimate.longEnough,
      },
      {
        id: 'personal',
        label: 'Non uguale alla tua email o al tuo nome',
        met: !empty && estimate.avoidsPersonal,
      },
      { id: 'common', label: 'Non fra le password più comuni o violate', met: null },
    ];
  });

  protected segmentClass(segment: number): string {
    const estimate = this.estimate();
    const color =
      segment <= estimate.score ? SEGMENT_COLORS[estimate.level] : 'bg-stone-200 dark:bg-stone-700';
    return `h-1.5 rounded-full transition-colors ${color}`;
  }
}
